"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";
import { cn } from "@/utils/utils";

export default function PronunciationExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= QUESTIONS ================= */
  const questions = Array.isArray(exercise.pronunciationQuestions)
    ? exercise.pronunciationQuestions.map((q) => ({
        textToPronounce: q.textToPronounce?.trim() || "",
        ipaSound: q.ipaSound?.trim() || "",
        requireCapitalization: !!q.requireCapitalization,
      }))
    : [];

  const speakText = (text, lang = "en-GB") => {
  if (typeof window === "undefined") return;

  const synth = window.speechSynthesis;
  if (!synth) return;

  synth.cancel(); // React 18 safety

  const speakNow = () => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.9;
    utterance.pitch = 1;

    const voices = synth.getVoices();
    const voice =
      voices.find(v => v.lang === lang) ||
      voices.find(v => v.lang.startsWith(lang.split("-")[0]));

    if (voice) utterance.voice = voice;

    synth.speak(utterance);
  };

  // 🔥 KEY FIX: wait for voices
  if (synth.getVoices().length === 0) {
    synth.onvoiceschanged = () => {
      synth.onvoiceschanged = null;
      speakNow();
    };
  } else {
    speakNow();
  }
};



  /* ================= SPEECH ================= */
  const recognition = useSpeechRecognition() || {};

  const {
    transcript = "",
    listening = false,
    startListening = () => {},
    resetTranscript = () => {},
    browserSupportsSpeechRecognition = false,
  } = recognition;

  const speechSupported =
    typeof window !== "undefined" &&
    (browserSupportsSpeechRecognition ||
      "SpeechRecognition" in window ||
      "webkitSpeechRecognition" in window);

  /* ================= STUDENT STATE ================= */
  const [answers, setAnswers] = useState({});
  const activeIndexRef = useRef(null);
  const completedRef = useRef(false);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[^\w\s]/g, "").replace(/\s+/g, " ");
  };

  /* ================= START MIC ================= */
  const handleStartListening = (idx) => {
    if (!speechSupported || listening) return;

    try {
      activeIndexRef.current = idx;
      resetTranscript();
      startListening({
        continuous: false,
        interimResults: false,
        language: "en-GB",
      });
    } catch (err) {
      console.error("Speech recognition failed:", err);
    }
  };

  /* ================= AUTO CHECK ================= */
  useEffect(() => {
    if (!isPreview || !transcript) return;

    const idx = activeIndexRef.current;
    if (idx === null || !questions[idx]) return;

    const q = questions[idx];

    const isCorrect =
      normalize(transcript, q.requireCapitalization) ===
      normalize(q.textToPronounce, q.requireCapitalization);

    setAnswers((prev) => {
      const next = {
        ...prev,
        [idx]: { spoken: transcript, isCorrect },
      };

      setAttempt({
        answers: next,
        hasInteracted: true,
      });

      const allAnswered =
        Object.keys(next).length === questions.length;
      const allCorrect =
        allAnswered &&
        Object.values(next).every((a) => a.isCorrect);

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;
        onComplete(exercise._id, allCorrect);
      }

      return next;
    });

    resetTranscript();
    activeIndexRef.current = null;
  }, [transcript]);

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    const updateQuestions = (next) =>
      onUpdate("pronunciationQuestions", next);

    const update = (idx, field, value) => {
      const next = [...questions];
      next[idx] = { ...next[idx], [field]: value };
      updateQuestions(next);
    };

    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6 space-y-4">
            <StringField
              label={`Text to Pronounce ${idx + 1}`}
              value={q.textToPronounce}
              onChange={(v) => update(idx, "textToPronounce", v)}
              required
              error={errors?.[`exercise-${exercise.index}-pron-text-${idx}`]}
            />

            {q.textToPronounce && (
             <SpeakerButton
              text={q.textToPronounce}
              lang="en-GB"
              onPlay={() => speakText(q.textToPronounce)}
            />

            )}

            <StringField
              label="IPA (optional)"
              value={q.ipaSound}
              onChange={(v) => update(idx, "ipaSound", v)}
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                update(idx, "requireCapitalization", c)
              }
            />
          </Card>
        ))}
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No pronunciation questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-base">
          Pronunciation Practice
        </CardTitle>
        <CardDescription>
          Click the mic and pronounce each word or sentence
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, idx) => {
          const result = answers[idx];

          return (
            <div
              key={idx}
              className="rounded-xl border p-6 text-center space-y-6"
            >
              <div className="text-3xl font-bold text-blue-700">
                {q.textToPronounce}
              </div>

              <SpeakerButton
              text={q.textToPronounce}
              lang="en-GB"
              onPlay={() => speakText(q.textToPronounce)}
            />

              {speechSupported ? (
                <Button
                  size="lg"
                  onClick={() => handleStartListening(idx)}
                  disabled={listening || showResult}
                  className="w-full max-w-md mx-auto"
                >
                  {listening
                    ? "Listening… 🎤"
                    : "🎤 Click to Speak"}
                </Button>
              ) : (
                <Input
                  placeholder="Type what you would say"
                  onBlur={(e) => {
                    const value = e.target.value;
                    const isCorrect =
                      normalize(value, q.requireCapitalization) ===
                      normalize(
                        q.textToPronounce,
                        q.requireCapitalization
                      );

                    setAnswers((prev) => ({
                      ...prev,
                      [idx]: { spoken: value, isCorrect },
                    }));
                  }}
                  className="max-w-md mx-auto"
                />
              )}

              {result && (
                <div
                  className={cn(
                    "text-lg font-semibold",
                    result.isCorrect
                      ? "text-green-700"
                      : "text-red-700"
                  )}
                >
                  {result.isCorrect
                    ? "Correct! ✅"
                    : "Try again 🔄"}
                  <div className="text-sm mt-1">
                    You said: <strong>{result.spoken}</strong>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
